import { type IoConfig, type PartialPropsOf, type PropsOf } from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';

import { AvatarComponent } from './avatar.component';
import { type AvatarDef } from './avatar.models';
import { getPropsConfig } from './avatar.props-config';
import { avatarV2PaddingToMarginMigration } from './migrations/002-avatar-padding-to-margin.migration';

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    avatar: AvatarDef;
  }
}

const DEFAULT_WIDTH = 332;
const DEFAULT_HEIGHT = 48;

registerElementDefinition({
  type: 'avatar',
  componentType: AvatarComponent,
  defaults: getDefaults(),
  groups: ['navigation'],
  height: { mode: 'auto', value: DEFAULT_HEIGHT },
  initial: getInitial(),
  ioConfig: getIoConfig(),
  isListed: true,
  name: 'User Profile',
  propsConfig: getPropsConfig(),
  styleIncludePaths: ['style'],
  version: 2,
  migrations: {
    2: avatarV2PaddingToMarginMigration,
  },
  width: { mode: 'auto', value: DEFAULT_WIDTH, fixed: false },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [],
  };
}

function getDefaults(): PropsOf<'avatar'> {
  return {
    content: {
      data: {
        source: 'Users',
      },
      fields: {
        avatarType: 'image',
        avatarTypeFallback: 'placeholder',
      },
      menu: {
        enabled: false,
        items: [],
      },
    },
    style: {
      label: {
        color: { color1: '$t.title', selectorType: 'solid' },
      },
      font: {
        style: [],
        fontSize: '$t.fontSize',
        fontFamily: '$t.fontFamily',
        lineHeight: '$t.lineHeight',
        fontWeight: '$t.fontWeight' as FontWeightOption,
      },
      general: {
        offset: { margin: '$t.spacingExternal' },
      },
      menu: {
        menuStateView: 'default',
        default: {
          label: { color1: '$t.title', selectorType: 'solid' },
          background: { color1: '$t.fill', selectorType: 'solid' },
          outline: { color1: '$t.outline', selectorType: 'none' },
        },
        hover: {
          label: { color1: '$t.title', selectorType: 'solid' },
          background: { color1: '$t.accent_a15', selectorType: 'solid' },
          outline: { color1: '$t.outline', selectorType: 'none' },
        },
        background: { color1: '$t.canvas', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'none' },
      },
      layout: {
        imageSize: `${DEFAULT_HEIGHT}px`,
        imagePosition: 'left',
        labelAlign: { horizontal: 'left' },
      },
      initials: {
        background: { color1: '$t.fill', selectorType: 'solid' },
        textColor: { color1: '$t.title', selectorType: 'solid' },
        font: {
          style: [],
          fontSize: '$t.fontSize',
          fontFamily: '$t.fontFamily',
          lineHeight: '$t.lineHeight',
          fontWeight: '$t.fontWeight' as FontWeightOption,
        },
      },
    },
  };
}

function getInitial(): PartialPropsOf<'avatar'> {
  return {};
}
